import React, { useState } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  TooltipHost,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useSubject } from '../Contexts/SubjectContext';
import { Button } from '@fluentui/react-components';
import { uploadProgressFile } from 'src/endpoints/postFile';
import { useEmployee } from '../Contexts/EmployeeContext';

const stackTokens = { childrenGap: 20, padding: 20 };
const columnTokens = { childrenGap: 10 };

const columnStyle = {
  root: {
    border: '1px solid #ccc',
    padding: 20,
    borderRadius: 5,
    width: '100%',
    maxWidth: '800px',
    boxSizing: 'border-box' as const,
  },
};

const EvaluationForm: React.FC = () => {
  const { subject } = useSubject();
  const { employee } = useEmployee();
  const navigate = useNavigate();

  const [evaluationFile, setEvaluationFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<string>('');

  // Allowed file extensions
  const allowedExtensions = ['pdf', 'xlsx', 'docx'];

  // If subject data is missing, navigate back
  if (!subject.SubjectName || !subject.Group || !subject.SubjectCode) {
    navigate(-1);
  }

  // Handle file selection with validation
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert(
          `Formato inválido. Solo se permiten archivos: ${allowedExtensions
            .map((ext) => '.' + ext)
            .join(', ')}`
        );
        return;
      }
      setEvaluationFile(file);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (evaluationFile) {
      setUploading(true);
      setUploadMessage('');
      try {
        await uploadProgressFile(
          evaluationFile,
          employee.EmployeeId,
          '4',
          subject
        );
        setUploadMessage('Forma de evaluación subida exitosamente.');
      } catch (error) {
        console.error('Error uploading forma de evaluación:', error);
        setUploadMessage('Error al subir forma de evaluación.');
      } finally {
        setUploading(false);
      }
    } else {
      alert('Seleccione un archivo para subir la forma de evaluación.');
    }
  };

  return (
    <Stack tokens={stackTokens}>
      <Stack horizontal horizontalAlign="start">
        <Button
          appearance="secondary"
          size="large"
          style={{
            backgroundColor: '#f5f5f5',
            border: '1px solid #ccc',
          }}
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
      </Stack>

      <Text variant="xLarge">Subir Forma de Evaluación</Text>

      {subject && (
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Materia: {subject.SubjectName}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Grupo: {subject.Group}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Clave Materia: {subject.SubjectCode}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Plan: {subject.Plan}
          </Text>
        </Stack>
      )}

      <Stack horizontalAlign="center" style={{ width: '100%' }}>
        <Stack
          horizontalAlign="center"
          tokens={columnTokens}
          styles={columnStyle}
        >
          <Text variant="large">Forma de Evaluación</Text>

          <TooltipHost
            content={`Formato permitido: ${allowedExtensions
              .map((ext) => '.' + ext)
              .join(', ')}`}
            styles={{ root: { display: 'inline-block' } }}
          >
            <input
              type="file"
              accept={allowedExtensions.map((ext) => '.' + ext).join(',')}
              onChange={handleFileChange}
            />
          </TooltipHost>

          <Stack horizontalAlign="center">
            <PrimaryButton
              text="Subir Forma Evaluación"
              onClick={handleUpload}
              disabled={uploading}
              styles={{
                root: {
                  minWidth: '150px',
                  maxWidth: '250px',
                },
              }}
            />
          </Stack>
        </Stack>

        <Stack tokens={stackTokens} horizontalAlign="center">
          <Text variant="xLarge">
            Puedes descargar un formato de evaluación ejemplo
          </Text>
          <DefaultButton
            text="Descargar Ejemplo"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/EjemploFormaEvaluacion.docx';
              link.download = 'EjemploFormaEvaluacion.docx';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          />
        </Stack>

        {uploading && <Spinner label="Subiendo archivo..." />}
      </Stack>

      {uploadMessage && (
        <MessageBar
          messageBarType={
            uploadMessage.includes('Error')
              ? MessageBarType.error
              : MessageBarType.success
          }
        >
          {uploadMessage}
        </MessageBar>
      )}
    </Stack>
  );
};

export default EvaluationForm;
